import { Link } from 'react-router-dom';
import { Icon, IconName } from '@hills-design-system/components';
import { TranslationKeyEnum } from '../../types/constants';
import { useTranslation } from '../../hooks/useTranslation';
import { useState } from 'react';
import { gigyaLogout } from '../../services/gigya';
import HroInformationModal from '../Modal/HroInformationModal';
import PetszelLearnMoreModal from '../Modal/PetszelLearnMoreModal';
import { analytics } from '../../services/analytics';
import { BoltIcon } from '../icons';
import { useShelterState } from '../../hooks/useShelter';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { getFoodShelterLoveRedirectUrl } from '../../utils/redirectUrl';

interface NavigationMenuProps {
  isOpen: boolean;
  onClose: () => void;
  missedReportsCount?: number;
}

export const NavigationMenu = ({
  isOpen,
  onClose,
  missedReportsCount = 0,
}: NavigationMenuProps) => {
  const { t } = useTranslation();
  const { shelter } = useShelterState();
  const { enableOrders, enablePetszelPromotion, enableAccountHomePage } = useFeatureFlags();

  const [openHroModal, setOpenHroModal] = useState({ visible: false, fullUrl: '' });
  const [isPetszelModalOpen, setIsPetszelModalOpen] = useState(false);
  const redirectUrl = getFoodShelterLoveRedirectUrl();

  const handleLogout = async () => {
    try {
      // Track logout action
      analytics.logout();
      // Clear Gigya session
      await gigyaLogout();

      onClose();
      // navigate to the public landing page after logout
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('[NavigationMenu] Logout error:', error);
      // navigate to the public landing page after logout
      window.location.href = redirectUrl;
    } finally {
      // Clear cached auth token so hasCachedAuth() returns false on reload
      localStorage.removeItem('hills_fsl_auth');
    }
  };

  const showHroInfoAndRedirect = () => {
    // Track orders CTA click
    analytics.navigate('/shelter-products', t(TranslationKeyEnum.Orders), 'subnav', null);

    const hroBaseUrl = import.meta.env.VITE_HRO_BASE_URL ?? 'https://dev.hillsretailorder.com';
    const fullUrl = `${hroBaseUrl}/fsl/shelter-products`;
    const hasSeenOrderIntegrationModal = localStorage.getItem('hasSeenOrderIntegrationModal');
    if (!hasSeenOrderIntegrationModal) {
      analytics.modalOpen('hro_information');
      setOpenHroModal({ visible: true, fullUrl });
      return;
    }

    window.open(fullUrl, '_blank');
    onClose();
  };

  return (
    <div
      className={`fixed inset-0 z-50 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
    >
      {/* Scrim/Overlay */}
      <button
        className="absolute inset-0 bg-black/50 border-0 p-0 cursor-default"
        onClick={onClose}
        aria-label="Close Navigation Menu"
      />

      {/* Menu Content */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-[min(426px,100vw)] flex justify-center pt-6 transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div
          className="bg-white rounded-2xl w-[min(378px,calc(100vw-48px))] relative flex flex-col overflow-hidden"
          style={{
            height: 'calc(100vh - 48px)',
            minHeight: '600px',
            maxHeight: 'calc(100vh - 48px)',
          }}
        >
          {/* Close Button - Always Visible */}
          <div className="sticky top-0 bg-white rounded-t-2xl p-8 pt-11 flex justify-end z-10">
            <button
              onClick={onClose}
              className="flex items-center justify-center h-6 gap-1"
              aria-label="Close menu"
            >
              <span className="font-semibold text-base text-black/90">Close</span>
              <Icon icon={IconName.X} size={16} />
            </button>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col px-6 md:px-8">
            {/* Primary Menu Items - Consistent Spacing */}
            <div className="flex-1 flex flex-col justify-center py-8">
              <div className="flex flex-col gap-4">
                <Link
                  to="/home"
                  onClick={() => {
                    analytics.navigate('/home', t(TranslationKeyEnum.Overview), 'subnav', null);
                    onClose();
                  }}
                  className="flex items-center h-12 rounded-lg group py-1.5"
                >
                  <span className="font-bold text-[32px] text-black/90 leading-[1.125] tracking-[-0.96px]">
                    {t(TranslationKeyEnum.Overview)}
                  </span>
                </Link>

                {enableOrders && (
                  <button
                    onClick={showHroInfoAndRedirect}
                    data-id="cta"
                    className="flex items-center h-12 rounded-lg group py-1.5"
                  >
                    <span className="font-bold text-[32px] text-black/90 leading-[1.125] tracking-[-0.96px]">
                      {t(TranslationKeyEnum.Orders)}
                    </span>
                    <Icon icon={IconName.ExternalLink} size={24} className="ml-2" />
                  </button>
                )}

                <div className="flex items-center h-12 rounded-lg relative py-1.5">
                  <Link
                    to="/reports"
                    onClick={() => {
                      analytics.navigate('/reports', t(TranslationKeyEnum.Reports), 'subnav', null);
                      onClose();
                    }}
                    className="flex items-center"
                  >
                    <span className="font-bold text-[32px] text-black/90 leading-[1.125] tracking-[-0.96px]">
                      {t(TranslationKeyEnum.Reports)}
                    </span>
                  </Link>
                  {/* Notification Badge — hidden for Petszel-enabled shelters (automated reporting) */}
                  {missedReportsCount > 0 && !shelter?.is_petszel_enabled && (
                    <div className="bg-[#ffd9d2] border border-[#fe4f32] rounded-full px-3.5 py-1 ml-2">
                      <span className="font-bold text-[14px] text-[#a81023] leading-[1.375]">
                        {missedReportsCount}
                      </span>
                    </div>
                  )}
                </div>

                <Link
                  to="/resources"
                  onClick={onClose}
                  className="flex items-center h-12 rounded-lg group"
                >
                  <span className="font-bold text-[32px] text-black/90 leading-[1.125] tracking-[-0.96px]">
                    Resources
                  </span>
                </Link>
              </div>
            </div>

            {/* Secondary Menu Items */}
            <div className="pb-4">
              <div className="flex flex-col gap-4">
                {enableAccountHomePage && (
                  <Link
                    to="/account"
                    onClick={() => {
                      analytics.navigate('/account', t(TranslationKeyEnum.Account));
                      onClose();
                    }}
                    className="flex items-start h-6 text-left"
                  >
                    <span className="font-semibold text-base text-black/90 leading-[1.125]">
                      {t(TranslationKeyEnum.Account)}
                    </span>
                  </Link>
                )}

                <Link
                  to="/help"
                  onClick={() => {
                    analytics.navigate('/help', t(TranslationKeyEnum.Help));
                    onClose();
                  }}
                  className="flex items-start h-6 text-left"
                >
                  <span className="font-semibold text-base text-black/90 leading-[1.125]">
                    {t(TranslationKeyEnum.Help)}
                  </span>
                </Link>
                <button onClick={handleLogout} className="flex items-start h-6 text-left">
                  <span className="font-semibold text-base text-black/90 leading-[1.125]">
                    {t(TranslationKeyEnum.Logout)}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Automate Your Reporting CTA - hidden when shelter already has automation enabled or when promotion is disabled */}
          {shelter && !shelter.is_petszel_enabled && enablePetszelPromotion && (
            <div className="px-4 pb-4 pt-6">
              <button
                onClick={() => setIsPetszelModalOpen(true)}
                className="bg-[#edf2f8] rounded-[16px] py-6 px-4 w-full flex items-center justify-center"
              >
                <span className="font-semibold text-base text-black/90 leading-[112.5%] font-montserrat">
                  <BoltIcon width={10} height={15} className="inline-block mr-[7px] align-middle" />
                  {t(TranslationKeyEnum.AutomateHillsReporting)}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Petszel Learn More Modal */}
      {isPetszelModalOpen && (
        <PetszelLearnMoreModal
          isOpen={isPetszelModalOpen}
          onClose={() => setIsPetszelModalOpen(false)}
        />
      )}

      {/* HRO Information Modal */}
      {openHroModal.visible && (
        <HroInformationModal
          isOpen={openHroModal.visible}
          onClose={() => {
            setOpenHroModal({ visible: false, fullUrl: '' });
            localStorage.setItem('hasSeenOrderIntegrationModal', 'true');
            window.open(openHroModal.fullUrl, '_blank');
          }}
          title={t(TranslationKeyEnum.HroInformationModalTitle)}
          description={t(TranslationKeyEnum.HroInformationModalDescription)}
          buttonLabel={t(TranslationKeyEnum.HroInformationModalButtonLabel)}
          onClick={() => {
            setOpenHroModal({ visible: false, fullUrl: '' });
            localStorage.setItem('hasSeenOrderIntegrationModal', 'true');
            window.open(openHroModal.fullUrl, '_blank');
            onClose();
          }}
        />
      )}
    </div>
  );
};
